/**
 * Bone deflection probability matrix — calibrated against Field Notes
 * A.2 (Sevr Hybrid quartering-into → deflected) and A.6 (BEAST mech
 * quartering-away + steep down → full pen + joint shatter).
 */

import type { PointType, ShotScenario } from '../trajectory/types';

interface DeflectionRow {
  scenario: ShotScenario;
  cocFixed: number;
  singleBevel: number;
  fixedMech: number;
  hybridMech: number;
  fullMech: number;
}

const DEFLECTION_TABLE: ReadonlyArray<DeflectionRow> = [
  { scenario: 'broadside-near',  cocFixed: 0.02, singleBevel: 0.03, fixedMech: 0.04, hybridMech: 0.05, fullMech: 0.07 },
  { scenario: 'broadside-far',   cocFixed: 0.03, singleBevel: 0.04, fixedMech: 0.05, hybridMech: 0.07, fullMech: 0.10 },
  { scenario: 'quartering-away', cocFixed: 0.04, singleBevel: 0.05, fixedMech: 0.07, hybridMech: 0.10, fullMech: 0.12 },
  /* ★ A.2 anchor — Sevr Hybrid 80lb 500gr quartering-into 30° → deflection */
  { scenario: 'quartering-into', cocFixed: 0.12, singleBevel: 0.18, fixedMech: 0.30, hybridMech: 0.55, fullMech: 0.55 },
  { scenario: 'frontal',         cocFixed: 0.25, singleBevel: 0.35, fixedMech: 0.45, hybridMech: 0.70, fullMech: 0.70 },
  { scenario: 'steep-down',      cocFixed: 0.10, singleBevel: 0.13, fixedMech: 0.18, hybridMech: 0.30, fullMech: 0.30 },
  { scenario: 'steep-up',        cocFixed: 0.08, singleBevel: 0.11, fixedMech: 0.15, hybridMech: 0.25, fullMech: 0.25 },
];

export const DEFLECTION_OUTCOME_PENETRATION: Record<PointType, number> = {
  'COC fixed':    0.55,
  'single-bevel': 0.45,
  'fixed mech':   0.35,
  'hybrid mech':  0.15,
  'full mech':    0.20,
};

export function deflectionProbability(
  scenario: ShotScenario,
  pointType: PointType
): number {
  const row = DEFLECTION_TABLE.find((r) => r.scenario === scenario);
  if (!row) return 0.10;
  switch (pointType) {
    case 'COC fixed':    return row.cocFixed;
    case 'single-bevel': return row.singleBevel;
    case 'fixed mech':   return row.fixedMech;
    case 'hybrid mech':  return row.hybridMech;
    case 'full mech':    return row.fullMech;
  }
}

export function effectivePenetrationFactor(
  scenario: ShotScenario,
  pointType: PointType
): number {
  const pDeflect = deflectionProbability(scenario, pointType);
  const pNoDeflect = 1 - pDeflect;
  return pNoDeflect * 1.0 + pDeflect * DEFLECTION_OUTCOME_PENETRATION[pointType];
}
